import React from 'react';
import {Image as RNImage, Platform, View, ScrollView} from 'react-native';
import {LoadableContent} from '../../shared/components/loadableContent';
import {Text} from '../../shared/components/text';
import {Breadcrumbs} from '../../shared/components/breadcrumbs';
import {Screens, Routes} from '../../shared/routing';
import {useStyles} from '../../shared/hooks/styles';
import {useIsDesktop} from '../../shared/hooks/useIsDesktop';
import {DeviceSize, maxDesktopContentWidth} from '../../shared/theme/helpers';
import {ViewProps} from './model';
import {Image} from '../../shared/components/image';
import {Footer} from './components/footer';
import Screen from '../../shared/components/screen';
import {CustomScrollView} from '../../shared/components/scrollView';
import {Html} from '../../shared/components/html';
import {normalizeUrl} from '../../shared/pipes';

const desktopMainContentWidth = 874;

const getRoutes = (title: string) => [
  {
    title: 'Последние новости',
    routeName: Screens.News,
    webRoute: Routes.news,
  },
  {
    title: title,
    routeName: Screens.NewsArticle,
    webRoute: Routes.newsArticle,
  },
];

export default ({prevNews, currentNews, nextNews, loading}: ViewProps) => {
  const scrollViewRef = React.useRef<ScrollView>(null);
  const isDesktop = useIsDesktop();
  const [mainImageHeight, setMainImageHeight] = React.useState(0);

  const styles = useStyles(({theme, addShadow, mediaOnly, mediaExclude}) => ({
    screen: {
      backgroundColor: theme.color.backgroundMenuColor,
    },
    main: {
      ...mediaOnly(DeviceSize.desktop, {
        paddingTop: 30,
        paddingBottom: 60,
        paddingHorizontal: 24,
      }),
      ...mediaExclude(DeviceSize.desktop, {
        paddingTop: 30,
        paddingHorizontal: 20,
        paddingBottom: 40,
      }),
      backgroundColor: theme.color.backgroundMenuColor,
      alignItems: 'center',
      width: '100%',
      margin: 'auto',
      maxWidth: maxDesktopContentWidth,
    },
    mainContent: {
      width: '100%',
      ...mediaOnly(DeviceSize.desktop, {
        paddingTop: 40,
        width: desktopMainContentWidth,
      }),
    },
    breadcrumbsContainer: {
      ...mediaOnly(DeviceSize.desktop, {
        flexDirection: 'row',
        width: '100%',
      }),
    },
    breadcrumbs: {
      ...mediaOnly(DeviceSize.desktop, {
        maxWidth: maxDesktopContentWidth - 120,
      }),
    },
    btnBack: {
      ...mediaOnly(DeviceSize.desktop, {
        width: 204,
        marginTop: 40,
      }),
      ...mediaExclude(DeviceSize.desktop, {
        width: '100%',
      }),
    },
    mainDate: {
      ...mediaOnly(
        DeviceSize.desktop,
        {
          marginTop: 5,
          marginBottom: 30,
        },
        {
          marginTop: 8,
          marginBottom: 16,
        },
      ),
      color: theme.color.labelText,
    },
    mainImageContainer: {
      ...addShadow({
        height: 2,
        radius: 5,
        opacity: 0.1,
      }),
      borderRadius: 5,
      overflow: 'hidden',
      backgroundColor: theme.color.backgroundMenuColor,
      marginBottom: 15,
    },
    mainDescription: {
      ...mediaOnly(DeviceSize.desktop, {
        paddingLeft: 28,
        marginVertical: 40,
      }),
      ...mediaExclude(DeviceSize.desktop, {
        paddingLeft: 15,
        marginVertical: 20,
      }),
      borderLeftWidth: 2,
      borderColor: theme.color.primaryColor,
    },
    mainText: mediaOnly(DeviceSize.mobile, {
      marginBottom: 10,
    }),
  }));

  React.useEffect(() => {
    if (Platform.OS === 'web') {
      window.scrollTo({top: 0});
    }
    if (!currentNews || !scrollViewRef.current) {
      return;
    }
    scrollViewRef.current.scrollTo({animated: false});
  }, [currentNews, scrollViewRef]);

  const setMainImageHeightByWidth = (width: number) => {
    const defaultHeight = (width / 3) * 2;
    if (currentNews?.image) {
      RNImage.getSize(
        normalizeUrl(currentNews.image),
        (resultWidth, resultHeight) => {
          setMainImageHeight((width / resultWidth) * resultHeight);
        },
        () => {
          setMainImageHeight(defaultHeight);
        },
      );
    } else {
      setMainImageHeight(defaultHeight);
    }
  };

  return (
    <Screen style={styles.screen} scrollViewRef={Platform.OS === 'web' ? scrollViewRef : undefined} fullWidth>
      <LoadableContent loading={loading}>
        <CustomScrollView
          contentInsetAdjustmentBehavior='automatic'
          ref={Platform.OS === 'web' ? undefined : scrollViewRef}>
          {currentNews && (
            <View style={styles.main}>
              <View style={styles.breadcrumbsContainer}>
                <Breadcrumbs routes={getRoutes(currentNews.name || '')} style={styles.breadcrumbs} />
              </View>
              <View
                style={styles.mainContent}
                onLayout={(event) => setMainImageHeightByWidth(event.nativeEvent.layout.width)}>
                <Text type={isDesktop ? 'h2' : 'h6'} testID='news-current-title'>
                  {currentNews.name}
                </Text>
                <Text type={isDesktop ? 'h5Light' : 'label'} style={styles.mainDate} testID='news-current-createdAt'>
                  {currentNews.createdAt}
                </Text>
                <View style={styles.mainImageContainer}>
                  {currentNews.image ? (
                    <Image
                      source={{uri: normalizeUrl(currentNews.image)}}
                      width={isDesktop ? desktopMainContentWidth : undefined}
                      height={mainImageHeight}
                      style={{height: mainImageHeight}}
                      resizeMode='contain'
                      testID='news-current-image'
                    />
                  ) : null}
                </View>
                <View style={styles.mainDescription} testID='news-current-annotation'>
                  <Html content={currentNews.description} testID='news-current-description' />
                </View>
                <View style={styles.mainText}>
                  <Html content={currentNews.text} testID='news-current-body' />
                </View>
              </View>
            </View>
          )}
          {prevNews && nextNews && <Footer prevNews={prevNews} nextNews={nextNews} />}
        </CustomScrollView>
      </LoadableContent>
    </Screen>
  );
};
